import { useState, useEffect } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../../App";
import { CheckCircle2, XCircle, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

const STATUS_CONFIG = {
  active:         { color: "#22C55E", bg: "#F0FDF4", border: "#BBF7D0", label: "Active",         icon: CheckCircle2 },
  connected:      { color: "#22C55E", bg: "#F0FDF4", border: "#BBF7D0", label: "Connected",      icon: CheckCircle2 },
  configured:     { color: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", label: "Configured",     icon: AlertTriangle },
  not_configured: { color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", label: "Not Configured", icon: XCircle },
  auth_failed:    { color: "#EAB308", bg: "#FFFBEB", border: "#FDE68A", label: "Auth Failed",    icon: AlertTriangle },
  unreachable:    { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Unreachable",    icon: XCircle },
  error:          { color: "#DC2626", bg: "#FEF2F2", border: "#FECACA", label: "Error",          icon: XCircle },
  timeout:        { color: "#EAB308", bg: "#FFFBEB", border: "#FDE68A", label: "Timeout",        icon: AlertTriangle },
  unknown:        { color: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", label: "Unknown",        icon: XCircle },
};

/**
 * Compact status pill for use in nav dropdowns and lists.
 * @param {{ status: string }} props
 */
export function IntegrationStatusPill({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: cfg.color }} />
      {cfg.label}
    </span>
  );
}

/**
 * Full health badge with icon, status text, optional last-sync, and test button.
 * Place at the top of integration config pages.
 *
 * @param {{ integration: "sap"|"ldap", onTestConnection?: () => Promise<{status:string, message:string}> }} props
 */
export function IntegrationHealthBadge({ integration, onTestConnection }) {
  const [status, setStatus] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const resp = await axios.get(`${API}/admin/integrations-status`, { headers: getAuthHeaders() });
        if (!mounted) return;
        const data = resp.data?.[integration];
        if (data) {
          setStatus(data.status);
          setLastSync(data.last_sync || null);
        }
      } catch { /* silent */ }
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [integration]);

  const handleTest = async () => {
    if (!onTestConnection) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await onTestConnection();
      setTestResult(result);
      if (result.status === "connected" || result.status === "success") {
        setStatus("active");
      } else {
        setStatus(result.status || "error");
      }
    } catch (err) {
      setTestResult({ status: "error", message: err.response?.data?.detail || "Test failed" });
      setStatus("error");
    } finally {
      setTesting(false);
    }
  };

  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;

  return (
    <div
      className="flex items-center justify-between p-4 border"
      style={{ backgroundColor: cfg.bg, borderColor: cfg.border }}
      data-testid={`integration-health-${integration}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} style={{ color: cfg.color }} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold" style={{ color: cfg.color }}>{cfg.label}</span>
            <IntegrationStatusPill status={status || "unknown"} />
          </div>
          {lastSync && (
            <p className="text-[10px] text-[#64748B] mt-0.5">
              Last sync: {new Date(lastSync).toLocaleString()}
            </p>
          )}
          {testResult && testResult.message && (
            <p className="text-xs mt-1" style={{ color: testResult.status === "connected" || testResult.status === "success" ? "#065F46" : "#991B1B" }}>
              {testResult.message}
            </p>
          )}
        </div>
      </div>
      {onTestConnection && (
        <button
          onClick={handleTest}
          disabled={testing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border transition-colors hover:opacity-80 disabled:opacity-50"
          style={{ borderColor: cfg.border, color: cfg.color }}
          data-testid={`test-${integration}-badge`}
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {testing ? "Testing..." : "Test"}
        </button>
      )}
    </div>
  );
}
